import AnalyticsEvent = ToroAnalyticsEnums.AnalyticsEvent;

import { Component, OnInit } from '@angular/core';
import { finalize, take } from 'rxjs/operators';
import { I3AssetsOperatingStatus, I3OperatingStatusAsset } from '../../../../../api/intelli360/models/i3-assets-operating-status.model';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { AuthManagerService } from '../../../../../api/auth/auth-manager.service';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { DashMessageService } from '../../../../../common/services/dash-message.service';
import { DashUserManagerService } from '../../../../../api/dash-user/dash-user-manager.service';
import { DeviceManagerService } from '../../../../../common/services/device-manager.service';
import { environment } from '../../../../../../environments/environment';
import { HttpStatusCode } from '@angular/common/http';
import { Intelli360ManagerService } from '../../../../../api/intelli360/intelli360-manager.service';
import { Intelli360WidgetBase } from '../_intelli360-widget-base';
import { ToroAnalyticsEnums } from '../../../../../common/enumerations/analytics.enums';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'toro-widget-intelli360-operating-status',
    templateUrl: './widget-intelli360-operating-status.component.html',
    styleUrls: ['./widget-intelli360-operating-status.component.less']
})
export class WidgetIntelli360OperatingStatusComponent extends Intelli360WidgetBase implements OnInit {

    iconColor = 'black';
    title = 'WIDGET.INTELLI360_OPERATING_STATUS';

    protected showDesktopModal = false;
    protected isDemoMode = false;
    protected assets: I3AssetsOperatingStatus;
    protected selectedAsset: I3OperatingStatusAsset;

    protected assetsOperatingCount = 0;
    protected assetsIdleCount = 0;
    protected assetsOffCount = 0;
    protected assetsTowingCount = 0;
    protected assetsWorkingCount = 0;
    protected assetsTransportingCount = 0;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(protected analyticsService: AnalyticsService,
                private authManager: AuthManagerService,
                protected broadcastService: BroadcastService,
                private dashMessageService: DashMessageService,
                protected dashUserManager: DashUserManagerService,
                protected deviceManager: DeviceManagerService,
                private intelli360Manager: Intelli360ManagerService,
                protected translateService: TranslateService,
    ) {
        super(analyticsService, broadcastService, dashUserManager, deviceManager, intelli360Manager, translateService);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.isDemoMode = environment.isDemoMode;
        if (this.isDemoMode) { this.title = ''; }
    }

    // =========================================================================================================================================================
    // Base Class Overrides
    // =========================================================================================================================================================

    get analyticsWidgetName(): string {
        return AnalyticsEvent.Intelli360MaintenanceWidgetName;
    }

    protected getWidgetData() {
        if (this.isDemoMode) {
            // In demo mode, we do not fetch real data. We show an image.
            this.isBusy = false;
            return;
        }

        if (!this.isIntelli360UserLoggedIn) { return; }
        if (this.authManager.isDoingIntelli360Login) { return; }
        if (this.isUnableToFetchData) { this.isBusy = true; }

        this.intelli360Manager.GetAssetsOperatingStatus()
            .pipe(
                take(1),
                finalize(() => {
                    this.isWidgetInitialized = true;
                    this.isBusy = false;
                })
            )
            .subscribe({
                next: (response: I3AssetsOperatingStatus) => {
                    this.assets = response;

                    // Get status counts
                    this.assetsOperatingCount = this.assets?.assetsModel?.filter(a => a.operationalStatus == "OPERATING")?.length || 0;
                    this.assetsIdleCount = this.assets?.assetsModel?.filter(a => a.operationalStatus == "IDLE")?.length || 0;
                    this.assetsOffCount = this.assets?.assetsModel?.filter(a => a.operationalStatus == "OFF")?.length || 0;
                    this.assetsTowingCount = this.assets?.assetsModel?.filter(a => a.operationalStatus == "TOWING")?.length || 0;
                    this.assetsWorkingCount = this.assets?.assetsModel?.filter(a => a.operationalStatus == "WORK")?.length || 0;
                    this.assetsTransportingCount = this.assets?.assetsModel?.filter(a => a.operationalStatus == "TRANSPORT")?.length || 0;

                    this.lastUpdateTimestamp = new Date();
                    this.clearIsUnableToFetchData();
                }, error: err => {
                    this.isIntelli360UserLoggedIn = err.status !== HttpStatusCode.FailedDependency;
                    if (!this.isIntelli360UserLoggedIn) { this.intelli360UserName = ''; }

                    this.setIsUnableToFetchData(!this.isIntelli360UserLoggedIn ? 'ERR_MSG.I3_USER_NOT_LOGGED_IN' : 'ERR_MSG.SERVER_ERROR');
                    if (this.isWidgetInitialized) { this.dashMessageService.showWidgetDataFetchErrorMessage(this.title); }
                }
            });
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onLinkClick() {
        this.broadcastService.toggleSystemOverlay.next({ show: true, text: 'STRINGS.NAVIGATING_TO_INTELLI360' });
        setTimeout(() => window.open(environment.intelli360SiteUrl, '_blank'), 1000);
        setTimeout(() => this.broadcastService.toggleSystemOverlay.next({ show: false }), 2000);
    }

    onShowMap() {
        this.onLinkClick();
    }

    onTableItemClick(tableItem: I3OperatingStatusAsset) {
        // TODO: SHOW ON MAP ??
        this.onLinkClick();
    }
}
